import React, { useMemo } from 'react';
import { PurchaseLot } from '../../../../../../types/entities';
import { LotsPurchaseTabProps } from '../LotsPurchaseTab.type';
import DescriptionList from '../../../../components/DescriptionList';
import DescriptionListItem from '../../../../components/DescriptionListItem';
import flatten from 'lodash/flatten';
import { useCustomNmcTitle } from '../../../../../Purchases/hooks/useCustomNmcTitle';
import { formatCurrency } from '../../../../../../utils/formatCurrency';
import LotTitle from './LotTitle';
import LotBoundToLots from './LotBoundToLots';
import { useGetPrice } from '../hooks/useGetPrice';
import LotRestrictions from './LotRestrictions';
import LotDeliveryPlace from './LotDeliveryPlace';
import LotMemberBenefits from './LotMemberBenefits';
import LotParams from './LotParams';
import LotItems from './LotItems';
import styles from './Lot.module.scss';
import { useIsBiddingForUnit } from '../../../../../Purchases/hooks/useIsBiddingForUnit';

interface Props extends LotsPurchaseTabProps {
    lot: PurchaseLot;
}

const Lot: React.FC<Props> = ({ purchase, lot, reference }) => {
    const customNmcTitle = useCustomNmcTitle(purchase);
    const isBidForUnit = useIsBiddingForUnit(purchase);
    const securityPrice = useGetPrice({
        amount: lot.securityAmount,
        currencyCaracterCode: lot.startPriceCurrency.characterCode,
        isPercent: lot.securityAmountIsPercent,
        percentValue: lot.securityAmountInPercentage,
        customNmcTitle,
    });
    const enforcementPrice = useGetPrice({
        amount: lot.enforcementSize,
        currencyCaracterCode: lot.startPriceCurrency.characterCode,
        isPercent: lot.enforcementSizeIsPercent,
        percentValue: lot.enforcementSizeInPercentage,
        customNmcTitle,
    });

    const showDelivery =
        !purchase?.isSeparateDelivery || !lot.isSeparateDelivery;
    const isProcedureRegPrequalification =
        purchase.procedure.reglament.name === 'prequalification';

    const isPropDisplayNmcFields = useMemo(() => {
        return (
            flatten(purchase.phases.map((phase) => phase.props)).find(
                (prop) => prop.name === 'display_nmc_fields'
            )?.value ?? true
        );
    }, [purchase]);

    const showSecurityPrice = Boolean(
        lot.securityAmountIsPercent
            ? lot.securityAmountInPercentage
            : lot.securityAmount
    );
    const showEnforcementPrice = Boolean(
        lot.enforcementSizeIsPercent
            ? lot.enforcementSizeInPercentage
            : lot.enforcementSize
    );

    return (
        <details open className={styles.container}>
            <LotTitle lot={lot} />

            <DescriptionList>
                <DescriptionListItem
                    title="Наименование лота"
                    definition={lot.title}
                />

                {isPropDisplayNmcFields &&
                    (isBidForUnit ? (
                        <>
                            <DescriptionListItem
                                title="Максимальное значение цены договора"
                                definition={
                                    lot?.maxContractPrice
                                        ? formatCurrency(
                                              lot?.maxContractPrice,
                                              lot.startPriceCurrency
                                                  .characterCode
                                          )
                                        : null
                                }
                            />
                            <DescriptionListItem
                                title="Начальная (максимальная) сумма цен за единицу"
                                definition={formatCurrency(
                                    lot?.startPrice,
                                    lot.startPriceCurrency.characterCode
                                )}
                            />
                        </>
                    ) : (
                        <DescriptionListItem
                            title={`${customNmcTitle.abbrNmcTitle} лота`}
                            definition={formatCurrency(
                                lot?.startPrice,
                                lot.startPriceCurrency.characterCode
                            )}
                        />
                    ))}

                {!!lot?.financingSource && (
                    <DescriptionListItem
                        title="Источники финансирования"
                        definition={lot?.financingSource}
                    />
                )}

                {!!lot?.goodsRequirement && (
                    <DescriptionListItem
                        title="Минимально необходимые требования, предъявляемые к закупаемым товарам (работам, услугам)"
                        definition={lot?.goodsRequirement}
                    />
                )}

                <DescriptionListItem
                    title="Тариф для участников"
                    definition={lot?.tariff?.name}
                />

                <LotBoundToLots lot={lot} />

                {showSecurityPrice && (
                    <DescriptionListItem
                        title="Размер обеспечения заявки"
                        definition={securityPrice}
                    />
                )}

                {showEnforcementPrice && (
                    <>
                        <DescriptionListItem
                            title="Сумма обеспечения исполнения договора"
                            definition={enforcementPrice}
                        />

                        {Boolean(lot.enforcementOrder) && (
                            <DescriptionListItem
                                title="Порядок обеспечения исполнения договора"
                                definition={lot.enforcementOrder}
                            />
                        )}
                    </>
                )}

                <LotRestrictions lot={lot} />
            </DescriptionList>

            {showDelivery && (
                <LotDeliveryPlace
                    reference={reference}
                    deliveryRegionCode={
                        purchase.isSeparateDelivery
                            ? lot.deliveryRegionCode
                            : purchase.deliveryRegionCode
                    }
                    deliveryAddress={
                        purchase.isSeparateDelivery
                            ? lot.deliveryAddress
                            : purchase.deliveryAddress
                    }
                    isProcedureRegPrequalification={
                        isProcedureRegPrequalification
                    }
                />
            )}

            <LotMemberBenefits lot={lot} />
            <LotParams lot={lot} />
            <LotItems
                lot={lot}
                reference={reference}
                isBidForUnit={isBidForUnit}
                isPropDisplayNmcFields={isPropDisplayNmcFields}
            />
        </details>
    );
};

export default React.memo(Lot);
